/* eslint-disable react/jsx-no-undef */
import React from 'react';
import { AxiosError } from 'axios';
import { Grid, Button } from '@material-ui/core';

import { ClientContext } from '../ClientContext';
import { ClientModel, clientModelInitial } from '../../../Models/ClientModel';
import ClientService from '../../../Services/ClientService';
import { EditAction } from '../../../shared/enum/EditAction';
import { HomeContext } from '../../Home/HomeContext';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { ClientErrorContext } from '../ClientErrorContext';
import { ClientPageTabs } from '../enums/ClientPageTabs';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';

export function ClientSave(){
    const clientContext = React.useContext(ClientContext);  
    const clientErrorContext = React.useContext(ClientErrorContext);  
    const homeContext = React.useContext(HomeContext);  
    
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = React.useState<boolean>(false);

    const clearClientInputs = () => {
        clientContext.setClient(clientModelInitial);
        clientContext.setVehicles([]);
        clientContext.setKeywords([]);
        clientContext.setSources([]);
        clientContext.setExtraParameters([]);
        clientContext.setContents([]);
        clientContext.setFlatOrganogram([]);
        clientContext.setClientClippers([]);
        clientContext.setCutouts([]);
        clientContext.setCutoutOrganogram([]);
        clientContext.setClientPage(ClientPageTabs.DEFAULT);
        clientContext.setClientAvatarFile(null);
        clientContext.setPrimaryColor('');
        clientContext.setSecondaryColor('');
        clientContext.setStockSymbol('');
        clientContext.setIdmConversionRateList([]);
        clientContext.setIrsConversionRateList([]);
    };

    const handleDiscard = () => {
        setIsDiscardDialogOpen(false);
        clearClientInputs();
        homeContext.setPageLocation(PageLocation.CLIENT_SEARCH);
    };

    const saveEditClient = (clientId: number, clientFormData: FormData) => {
        ClientService.updateClient(clientId, clientFormData)
            .then(() => {
                homeContext.showSuccess('Cliente alterado com sucesso!');

                clearClientInputs();
                homeContext.setPageLocation(PageLocation.CLIENT_SEARCH);
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops... ocorreu um erro e não foi possível editar o cliente.'
                    + ' Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            })
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const saveNewClient = (clientFormData: FormData) => {
        ClientService.createClient(clientFormData)
            .then(() => {
                homeContext.showSuccess('O novo cliente foi incluído com sucesso!');
                clearClientInputs();
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. Não foi possível salvar o novo cliente. ', error);
            })
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const handleSave = () => { 
        if(!hasError()) {
            const client: ClientModel = {...clientContext.client};
            client.vehicles = clientContext.vehicles;
            client.keywords = clientContext.keywords;
            client.sources = clientContext.sources;
            client.extraParameters = clientContext.extraParameters;
            client.contents = clientContext.contents;
            client.flatOrganogram = clientContext.flatOrganogram;
            client.clientClippers = clientContext.clientClippers;
            client.cutouts = clientContext.cutouts;
            client.idmConversionRates = clientContext.idmConversionRateList;
            client.irsConversionRates = clientContext.irsConversionRateList;
            client.primaryColor = clientContext.primaryColor ?? '';
            client.secondaryColor = clientContext.secondaryColor ?? '';
            client.stockSymbol = clientContext.stockSymbol;
            client.products = clientContext.products;

            const clientFormData = new FormData();
            clientFormData.append('documentData', JSON.stringify(client));

            if (clientContext.clientAvatarFile != null)
                clientFormData.append('file', clientContext.clientAvatarFile);

            homeContext.setOpenLoadingDialog(true);

            if (clientContext.editMode) 
                saveEditClient(client.id, clientFormData);
            else 
                saveNewClient(clientFormData);
        }
    };

    const hasError = () => {
        let error = false;

        const nameError = !clientContext.client.name;
        clientErrorContext.setNameNotDefinedError(nameError);

        const productError = !(clientContext.client.products &&
            clientContext.products.filter(p => p.editAction != EditAction.DELETE).length > 0);
        clientErrorContext.setProductNotSelectedError(productError);

        const hasIdmProduct = clientContext.products.some(p => (p.productDescription === 'IDM' && p.editAction != EditAction.DELETE) );
        const hasSocialMediaProduct = clientContext.products.some(p => (p.productDescription === 'Redes Sociais' && p.editAction != EditAction.DELETE));

        const vehicleError = hasIdmProduct && !(clientContext.vehicles && clientContext.vehicles.length > 0);
        clientErrorContext.setVehicleNotDefinedError(vehicleError);

        const keywordError = hasIdmProduct && !(clientContext.keywords && clientContext.keywords.filter(k =>
            k.editAction != EditAction.DELETE && k.active).length > 0);
        clientErrorContext.setKeywordsNotDefinedError(keywordError);

        const sourceError = hasIdmProduct && !(clientContext.sources && clientContext.sources.filter(k =>
            k.editAction != EditAction.DELETE && k.endOfPeriod == null).length > 0);
        clientErrorContext.setSourceNotDefinedError(sourceError);

        const extraPrameterError = hasIdmProduct && !(clientContext.extraParameters && clientContext.extraParameters.filter(k =>
            k.editAction != EditAction.DELETE && k.active).length > 0);
        clientErrorContext.setExtraParameterNotDefinedError(extraPrameterError);

        const contentError = hasIdmProduct && !(clientContext.contents && clientContext.contents.length > 0);
        clientErrorContext.setContentNotDefinedError(contentError);

        const avatarError = hasSocialMediaProduct && !clientContext.clientAvatarFile && !clientContext.client.imageUrl;
        clientErrorContext.setAvatarFileError(avatarError);

        const primaryColorNotDefinedError = hasSocialMediaProduct && !clientContext.primaryColor;
        clientErrorContext.setPrimaryColorNotDefinedError(primaryColorNotDefinedError);

        const primaryColorError = clientErrorContext.primaryColorError;
        
        const secondaryColorNotDefinedError = hasSocialMediaProduct && !clientContext.secondaryColor;
        clientErrorContext.setSecondaryColorNotDefinedError(secondaryColorNotDefinedError);

        const secondaryColorError = clientErrorContext.secondaryColorError;

        error = nameError ||
            productError ||
            vehicleError ||
            keywordError ||
            sourceError ||
            extraPrameterError ||
            contentError ||
            avatarError ||
            primaryColorNotDefinedError ||
            primaryColorError ||
            secondaryColorNotDefinedError ||
            secondaryColorError;

        return error;
    };

    const getDiscardButton = () => {
        return (
            <Grid item>
                <Button                                
                    variant='outlined'
                    color='primary'
                    size='large'
                    onClick={() => setIsDiscardDialogOpen(true)}>
                    Descartar
                </Button>
            </Grid>
        );
    };

    const getSaveButton = () => {
        return (
            <Grid item>
                <Button                                
                    variant='contained'
                    color='primary'
                    size='large'
                    onClick={handleSave}>
                    {!(clientContext.editMode) ? 'Salvar novo cliente' : 'Salvar alterações'}
                </Button>
            </Grid>
        );
    };

    return (
        <>
            <Grid item xs={12}>
                <Grid container
                    spacing={1}
                    direction="row"
                    justify="flex-end"
                    alignItems="flex-end">
                    { (clientContext.editMode) && getDiscardButton() }
                    { getSaveButton() }
                </Grid>
            </Grid>
        
            <BasicConfirmDialog
                title='Descartar as alterações realizadas?'
                description={'Ao descartar as alterações do cliente serão perdidas.'}
                handleFirstButton={() => setIsDiscardDialogOpen(false)}
                handleSecondButton={handleDiscard}
                textFirstButton="Manter"
                textSecondButton="Descartar"
                open={isDiscardDialogOpen} />
        </>
    );
}